import { Card } from '../../../../components/card/card';
import { Divider } from '../../../../components/divider/divider';
import { Typography } from '../../../../components/typography/typography';
import './product-inventory.scss';
import '../../inventory-add-edit.scss';
import { ProductInventoryType } from '../../hooks/use-add-product-data';
import { Toggle } from '../../../../components/form/toggle';
import { Location } from '../../../../clients/quipli-service/location/location';
import { LocationTable } from './location-table';
import { PrettyDescriptor } from '../../../../components/pretty-descriptor/pretty-descriptor';
import classNames from 'classnames';
import SkeletonLoader from '../../../../components/skeleton-loader/skeleton-loader';
import { CreatePartRequest } from '../../../../clients/quipli-service/product/product';

export const ProductInventory = ({
  productNumber,
  setProductNumber,
  selectedLocationIds,
  setSelectedLocationIds,
  productInventory,
  setProductInventory,
  allLocations,
  isInitiallyLoading,
  handleBlur,
  isSaving,
  type,
  isEditing,
  purchasePrice,
  setPurchasePrice,
}: {
  productNumber: string;
  setProductNumber: (productNumber: string) => void;
  selectedLocationIds: number[];
  setSelectedLocationIds: (value: number[]) => void;
  productInventory: ProductInventoryType[];
  setProductInventory: (tableProductInventory: ProductInventoryType, locationId: number) => void;
  allLocations: Location[];
  isInitiallyLoading: boolean;
  handleBlur: (key: keyof CreatePartRequest, value: any) => Promise<void>;
  isSaving: boolean;
  type: 'Part' | 'Service';
  isEditing: boolean;
  purchasePrice: number;
  setPurchasePrice: (value: React.SetStateAction<number>) => void;
}) => {
  return (
    <Card className="card gap-4">
      <div className="part-inventory-title-card">
        <Typography type="text-xl" weight="semi-bold">
          Inventory
        </Typography>
        <Divider />
      </div>

      {isInitiallyLoading ? (
        <SkeletonLoader style={{ width: '--webkit-fill-available', height: '20vh', paddingTop: '2rem' }} />
      ) : (
        <>
          {type === 'Part' && (
            <div className="part-inventory-part-number flex flex-col md:flex-row mb-2">
              <PrettyDescriptor
                title="Part Number"
                description="Enter a unique ID to track this part in your inventory."
                required
              />
              <div className="mt-4 ml-0 md:ml-4">
                <input
                  value={productNumber}
                  onChange={(e) => setProductNumber(e.target.value)}
                  onBlur={(e) => handleBlur('sku', e.target.value)}
                  className="w-auto h-[2.2rem]"
                  disabled={isSaving}
                  data-cy="part-number"
                  placeholder="Enter part number"
                />
              </div>
            </div>
          )}

          <Divider />

          {/* Portion for enabling/disabling locations */}

          {isEditing && (
            <div
              className={classNames('flex flex-col xxl:flex-row', type === 'Part' ? 'justify-around' : 'justify-start')}
            >
              <div className="part-inventory-locations flex flex-col md:flex-row xxl:flex-col mt-2">
                <PrettyDescriptor
                  title="Location Availability"
                  description={`Select all locations where this ${type.toLowerCase()} is available for purchase.`}
                  required
                />

                <div className="location-edit w-full md:w-2/3 ml-0 md:ml-4 xxl:ml-0">
                  <div className="enable-disable">
                    <p
                      className="location-action"
                      onClick={() => !isSaving && setSelectedLocationIds(allLocations.map((l) => l.id))}
                      data-cy="parts-location-enable-all"
                    >
                      <Typography type="text-md">Enable All</Typography>
                    </p>
                  </div>
                  <div className="location-select-section overflow-auto max-h-auto md:max-h-[25vh]">
                    {allLocations.map((l: Location, index) => (
                      <LocationToggle
                        location={l}
                        setSelectedLocationIds={setSelectedLocationIds}
                        selectedLocationIds={selectedLocationIds}
                        isSaving={isSaving}
                        key={index}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {type === 'Part' && (
                <div>
                  <div className="block xl:hidden">
                    <Divider />
                  </div>
                  <div className="part-inventory-part-number flex flex-col md:flex-row mb-4 mt-0 xxl:mt-2">
                    <PrettyDescriptor
                      title="Purchase Price"
                      description={`Enter the price this ${type.toLowerCase()} was purchased for.`}
                      required
                    />
                    <div className="mt-4 ml-0 md:ml-4">
                      <input
                        value={purchasePrice}
                        className="conditional-input"
                        type="number"
                        onWheel={(e) => e.currentTarget.blur()}
                        onChange={(e) => setPurchasePrice(parseFloat(e.target.value))}
                        onBlur={(e) => handleBlur('purchasePrice', parseFloat(e.target.value) ?? 0)}
                        disabled={isSaving}
                        data-cy="part-purchase-price"
                      />
                    </div>
                  </div>
                  <LocationTable
                    partInventory={productInventory}
                    setPartInventory={setProductInventory}
                    allLocations={allLocations}
                    isSaving={isSaving}
                  />
                </div>
              )}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

const LocationToggle = ({
  location,
  setSelectedLocationIds,
  selectedLocationIds,
  isSaving,
}: {
  location: Location;
  setSelectedLocationIds: (value: number[]) => void;
  selectedLocationIds: number[];
  isSaving: boolean;
}) => {
  return (
    <div
      key={location.id}
      className="flex flex-row gap-2 mb-4 cursor-pointer"
      onClick={() => {
        if (isSaving) {
          return;
        }
        setSelectedLocationIds([location.id]);
      }}
    >
      <Toggle
        color="var(--quipli-yellow-300)"
        data-cy="part-location-toggle"
        checked={selectedLocationIds.includes(location.id)}
        disabled={isSaving}
      />
      <Typography type="text-md" className="break-words whitespace-normal max-w-[12rem]">
        {location.name}
      </Typography>
    </div>
  );
};
